/**
 * SettingsControls component - Settings button and user avatar
 * Factory function following the app's architectural patterns
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import { createAvatar, type AvatarAPI } from "@UI/Avatar/index.js";
import { logout } from "@/api.js";
import type { Session } from "@/types/session";
import type { DominantVisibilityConfig } from "@/components/Stage/subcomponents/UIControlsLayer/visibility-profiles";

export interface SettingsControlsConfig {
  readonly onSettingsClick?: () => void;
  readonly onPageSettingsClick?: () => void;
  readonly onAvatarClick?: () => void;
  readonly userInitial?: string;
  readonly session?: Readonly<Session | null>;
}

export interface SettingsControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setUserInitial: (initial: string) => void;
  readonly setSettingsDisabled: (disabled: boolean) => void;
  readonly setPageSettingsDisabled: (disabled: boolean) => void;
  readonly setUser: (
    user: { name?: string; email?: string; avatarUrl?: string } | null
  ) => void;
  readonly applyVisibility: (
    visibility: DominantVisibilityConfig["settings"]
  ) => void;
  readonly destroy: () => void;
}

/**
 * Creates SettingsControls component with settings button and avatar
 * @param config - Configuration for the settings controls
 * @returns SettingsControls API
 */
export const createSettingsControls = (
  config: SettingsControlsConfig = {}
): SettingsControlsAPI => {
  const {
    onSettingsClick,
    onPageSettingsClick,
    onAvatarClick,
    userInitial = "U",
    session,
  } = config;

  let isDestroyed = false;
  let settingsButtonAPI: IconButtonAPI | null = null;
  let pageSettingsButtonAPI: IconButtonAPI | null = null;
  let avatarAPI: AvatarAPI | null = null;
  let avatarClickInProgress = false;

  const handleDefaultAvatarClick = async (): Promise<void> => {
    if (avatarClickInProgress || isDestroyed) return;
    console.log("handleDefaultAvatarClick");
    avatarClickInProgress = true;
    try {
      await logout();
    } catch (err) {
      // Intentionally ignore errors; we still route to auth
      console.error("Logout failed", err);
    } finally {
      try {
        if (typeof window !== "undefined") {
          window.history.pushState({}, "", "/auth");
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      } catch {}
      avatarClickInProgress = false;
    }
  };

  // Create container element
  const container = document.createElement("div");
  container.className = "settings-controls";

  /**
   * Initialize the settings controls with button and avatar
   */
  const initSettingsControls = (): void => {
    try {
      // Create settings button
      pageSettingsButtonAPI = createIconButton({
        icon: "prefs_settings",
        size: 44,
        iconSize: 28,
        behavior: "button",
        className: "settings-button",
        ...(onSettingsClick && { onClick: onSettingsClick }),
      });

      settingsButtonAPI = createIconButton({
        icon: "prefs_project",
        size: 44,
        iconSize: 28,
        behavior: "button",
        className: "settings-button",
        ...(onPageSettingsClick && { onClick: onPageSettingsClick }),
      });

      // Create user avatar
      // If a custom onAvatarClick is provided, call it and then run default logout
      const composedAvatarClick = onAvatarClick
        ? async () => {
            try {
              onAvatarClick();
            } finally {
              await handleDefaultAvatarClick();
            }
          }
        : handleDefaultAvatarClick;

      const user = (session as any)?.user || {};
      avatarAPI = createAvatar({
        initial: userInitial,
        name: user.name,
        email: user.email,
        avatarUrl: user.avatarUrl,
        size: 44,
        backgroundColor: "var(--color-dark)",
        textColor: "var(--color-text--inverse)",
        fontSize: 14,
        className: "user-avatar",
        onClick: composedAvatarClick,
      });

      // Append components to container
      container.appendChild(pageSettingsButtonAPI.getElement());
      container.appendChild(settingsButtonAPI.getElement());
      container.appendChild(avatarAPI.getElement());
    } catch (error) {
      console.error("Failed to create SettingsControls component:", error);
      throw error;
    }
  };

  /**
   * Update user initial
   */
  const setUserInitial = (initial: string): void => {
    if (isDestroyed || !avatarAPI) return;
    avatarAPI.setInitial(initial);
  };

  const setUser = (
    user: { name?: string; email?: string; avatarUrl?: string } | null
  ): void => {
    if (isDestroyed || !avatarAPI) return;
    const u = user || {};
    if (typeof u.name === "string") avatarAPI.setName(u.name);
    if (typeof u.email === "string") avatarAPI.setEmail(u.email);
    if (typeof u.avatarUrl === "string") avatarAPI.setImageUrl(u.avatarUrl);
  };

  /**
   * Set settings button disabled state
   */
  const setSettingsDisabled = (disabled: boolean): void => {
    if (isDestroyed || !settingsButtonAPI) return;
    settingsButtonAPI.setDisabled(disabled);
  };

  /**
   * Set page settings button disabled state
   */
  const setPageSettingsDisabled = (disabled: boolean): void => {
    if (isDestroyed || !pageSettingsButtonAPI) return;
    pageSettingsButtonAPI.setDisabled(disabled);
  };

  const applyVisibility = (
    visibility: DominantVisibilityConfig["settings"]
  ): void => {
    if (isDestroyed) return;

    const isEntryVisible = (
      entry:
        | DominantVisibilityConfig["settings"][keyof DominantVisibilityConfig["settings"]]
        | undefined
    ): boolean => !!entry && entry.visible !== false;

    const primaryEntry = isEntryVisible(visibility.page)
      ? visibility.page
      : isEntryVisible(visibility.sketch)
      ? visibility.sketch
      : null;

    const pageButtonEl = pageSettingsButtonAPI?.getElement();
    if (pageButtonEl) {
      pageButtonEl.style.display = primaryEntry ? "" : "none";
      if (primaryEntry?.label) {
        pageButtonEl.setAttribute("title", primaryEntry.label);
      } else {
        pageButtonEl.removeAttribute("title");
      }
    }

    const projectButtonEl = settingsButtonAPI?.getElement();
    const projectVisible = isEntryVisible(visibility.project);
    if (projectButtonEl) {
      projectButtonEl.style.display = projectVisible ? "" : "none";
      if (visibility.project?.label) {
        projectButtonEl.setAttribute("title", visibility.project.label);
      } else {
        projectButtonEl.removeAttribute("title");
      }
    }

    const avatarEl = avatarAPI?.getElement();
    const userVisible = isEntryVisible(visibility.user);
    if (avatarEl) {
      avatarEl.style.display = userVisible ? "" : "none";
      if (visibility.user?.label) {
        avatarEl.setAttribute("title", visibility.user.label);
      } else {
        avatarEl.removeAttribute("title");
      }
    }

    const anyVisible = !!primaryEntry || projectVisible || userVisible;
    container.style.display = anyVisible ? "" : "none";
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy components
    settingsButtonAPI?.destroy();
    pageSettingsButtonAPI?.destroy();
    avatarAPI?.destroy();

    // Clear references
    settingsButtonAPI = null;
    pageSettingsButtonAPI = null;
    avatarAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initSettingsControls();

  return {
    getElement: () => container,
    setUserInitial,
    setSettingsDisabled,
    setPageSettingsDisabled,
    setUser,
    applyVisibility,
    destroy,
  };
};

/**
 * Creates SettingsControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the settings controls
 * @returns SettingsControls API
 */
export const createSettingsControlsInContainer = (
  container: HTMLElement,
  config: SettingsControlsConfig = {}
): SettingsControlsAPI => {
  const settingsControls = createSettingsControls(config);
  container.appendChild(settingsControls.getElement());
  return settingsControls;
};
